import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MessageCircle, Loader2, ShieldCheck } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleLogin() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function LoginPage() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/inbox" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    await login(email, password);
    setBusy(false);
  };

  return (
    <div className="min-h-screen w-full flex bg-background">
      {/* Left panel - brand */}
      <div className="hidden md:flex md:w-1/2 lg:w-3/5 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-700 via-emerald-800 to-slate-900" />
        <div className="absolute inset-0"
             style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
        <div className="relative z-10 p-12 lg:p-16 flex flex-col justify-between text-white w-full">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center">
              <MessageCircle className="w-6 h-6" strokeWidth={2.2} />
            </div>
            <div>
              <div className="heading text-xl font-bold">ContaZap - Vendor</div>
              <div className="text-xs text-emerald-100/80">Atendimento e vendas pelo WhatsApp</div>
            </div>
          </div>

          <div className="max-w-lg">
            <h1 className="heading text-4xl lg:text-5xl font-extrabold leading-tight mb-6">
              A central de atendimento e vendas da sua empresa.
            </h1>
            <p className="text-emerald-50/90 text-lg leading-relaxed">
              Conecte vários números do WhatsApp, distribua conversas por equipe, transforme mensagens em oportunidades no CRM e acompanhe cada retorno — para qualquer tipo de negócio.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              {[
                ["Multi-conexões", "Vários WhatsApp em um único painel"],
                ["Multi-atendentes", "Filas por setor e distribuição automática"],
                ["CRM de vendas", "Funil personalizável e retornos agendados"],
                ["Notas internas", "Comunicação privada da equipe"],
              ].map(([t, d]) => (
                <div key={t} className="p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="heading font-semibold text-sm">{t}</div>
                  <div className="text-xs text-emerald-100/70 mt-1">{d}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs text-emerald-100/70">
            <ShieldCheck className="w-4 h-4" />
            Login seguro via JWT · Dados criptografados
          </div>
        </div>
      </div>

      {/* Right - form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <form onSubmit={submit} className="w-full max-w-md space-y-6">
          <div className="md:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center text-white">
              <MessageCircle className="w-5 h-5" />
            </div>
            <div className="heading text-lg font-bold">ContaZap - Vendor</div>
          </div>

          <div>
            <h2 className="heading text-2xl sm:text-3xl font-bold">Entrar</h2>
            <p className="text-sm text-muted-foreground mt-2">
              Acesse a central de atendimento da sua empresa.
            </p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email" type="email" required
                data-testid="login-email-input"
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="voce@suaempresa.com.br"
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password" type="password" required
                data-testid="login-password-input"
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-11"
              />
            </div>
          </div>

          <Button
            type="submit" disabled={busy}
            data-testid="login-submit-button"
            className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Entrar na central"}
          </Button>

          <div className="relative my-2">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center text-[11px] uppercase tracking-wider">
              <span className="bg-background px-2 text-muted-foreground">ou</span>
            </div>
          </div>

          <Button
            type="button" variant="outline"
            onClick={startGoogleLogin}
            data-testid="login-google-button"
            className="w-full h-11 font-semibold"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
            </svg>
            Entrar com Google
          </Button>

          <div className="text-xs text-muted-foreground text-center pt-2">
            ContaZap - Vendor · cada empresa acessa somente os próprios dados
          </div>
        </form>
      </div>
    </div>
  );
}
